import { Component, Output, Input, EventEmitter, ViewChild, ElementRef, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, NgZone } from '@angular/core';
import { NewTripExtractorService, ExtractedTripData, SupportedFileType } from '../../service/ai-assistant-service/new-trip-extractor.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-new-trip-import-modal',
  templateUrl: './new-trip-import-modal.component.html',
  styleUrls: ['./new-trip-import-modal.component.css']
})
export class NewTripImportModalComponent implements OnInit, OnChanges {
  @Input() fundingSources: any[] = [];
  @Input() initialFundingSourceId: string = '';
  @Output() onClose = new EventEmitter<void>();
  @Output() onTripExtracted = new EventEmitter<ExtractedTripData>();
  @ViewChild('fileInput', { static: false }) fileInput!: ElementRef;

  selectedFundingSourceId: string = '';
  isFsRequiredError = false;
  selectedFile: File | null = null;
  isLoading = false;
  isDragOver = false;
  errorMessage = '';
  extractedTrips: ExtractedTripData[] = [];
  selectedTripIndex: number = 0;

  // File Preview & Type
  fileType: SupportedFileType = 'unknown';
  imagePreviewUrl: string | null = null;

  allowedExtensions = ['.xlsx', '.xls', '.csv', '.pdf', '.png', '.jpg', '.jpeg', '.webp', '.txt'];

  constructor(
    private newTripExtractorService: NewTripExtractorService,
    private toastr: ToastrService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone
  ) { }

  ngOnInit() {
    if (this.initialFundingSourceId) {
      this.selectedFundingSourceId = this.initialFundingSourceId;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.initialFundingSourceId && changes.initialFundingSourceId.currentValue) {
      this.selectedFundingSourceId = changes.initialFundingSourceId.currentValue;
    }
  }

  onFundingSourceChanged() {
    this.isFsRequiredError = false;
    if (this.selectedFundingSourceId && this.extractedTrips && this.extractedTrips.length > 0) {
      const selectedFs = this.fundingSources ? this.fundingSources.find(f => f._id === this.selectedFundingSourceId) : null;
      this.extractedTrips.forEach(t => {
        t.fundingSourceId = this.selectedFundingSourceId;
        t.fundingSource = selectedFs ? (selectedFs.displayName || selectedFs.name || selectedFs.companyName) : '';
        t.fundingSourceObj = selectedFs;
      });
    }
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
    if (event.dataTransfer && event.dataTransfer.files && event.dataTransfer.files.length > 0) {
      this.handleFile(event.dataTransfer.files[0]);
    }
  }

  onFileInputChange(event: any) {
    if (event.target && event.target.files && event.target.files.length > 0) {
      this.handleFile(event.target.files[0]);
    }
  }

  handleFile(file: File) {
    this.errorMessage = '';
    const extension = '.' + file.name.split('.').pop()!.toLowerCase();
    if (!this.allowedExtensions.includes(extension)) {
      this.errorMessage = `Unsupported file format (${extension}). Please upload an Image (.png, .jpg), PDF (.pdf), Excel (.xlsx, .xls, .csv), or Text (.txt) file.`;
      return;
    }

    this.selectedFile = file;
    this.fileType = this.newTripExtractorService.detectFileType(file);
    this.extractedTrips = [];
    this.selectedTripIndex = 0;
    this.imagePreviewUrl = null;

    if (this.fileType === 'image') {
      const reader = new FileReader();
      reader.onload = () => {
        this.imagePreviewUrl = reader.result as string;
      };
      reader.readAsDataURL(file);
    }

    this.extractData();
  }

  async extractData() {
    if (!this.selectedFile) return;

    this.isLoading = true;
    this.errorMessage = '';
    this.cdr.detectChanges();

    try {
      const selectedFs = this.fundingSources ? this.fundingSources.find(f => f._id === this.selectedFundingSourceId) : null;
      const trips = await this.newTripExtractorService.extractTripFromFile(this.selectedFile, {
        selectedFundingSource: selectedFs
      });

      this.ngZone.run(() => {
        this.isLoading = false;
        if (!trips || trips.length === 0) {
          this.errorMessage = 'No trip information could be extracted from this document. Please verify the file content or try another document.';
          this.cdr.detectChanges();
          return;
        }

        // If no funding source is pre-selected yet, attempt auto-detect matching from extracted document
        if (!this.selectedFundingSourceId && trips && trips.length > 0 && this.fundingSources && this.fundingSources.length > 0) {
          const docFs = (trips[0].fundingSource || (trips[0] as any).healthplanCode || '').toLowerCase().trim();
          if (docFs) {
            const matched = this.fundingSources.find(f => {
              const dName = (f.displayName || f.name || f.companyName || '').toLowerCase().trim();
              const code = (f.code || f.healthplanCode || '').toLowerCase().trim();
              return dName === docFs || code === docFs || dName.includes(docFs) || docFs.includes(dName);
            });
            if (matched) {
              this.selectedFundingSourceId = matched._id;
            }
          }
        }

        // Apply the active funding source to all extracted trips
        if (this.selectedFundingSourceId && this.fundingSources) {
          const activeFs = this.fundingSources.find(f => f._id === this.selectedFundingSourceId);
          if (activeFs) {
            trips.forEach(t => {
              t.fundingSourceId = this.selectedFundingSourceId;
              t.fundingSource = activeFs.displayName || activeFs.name || activeFs.companyName || '';
              t.fundingSourceObj = activeFs;
            });
          }
        }

        this.extractedTrips = trips;
        this.selectedTripIndex = 0;

        if (this.extractedTrips.length === 1) {
          this.toastr.success('Trip data scraped successfully with AI!', 'AI Extraction');
        } else {
          this.toastr.info(`Found ${this.extractedTrips.length} trip rows in document. Please select one to import.`, 'AI Parsed');
        }
        this.cdr.detectChanges();
      });
    } catch (err) {
      this.ngZone.run(() => {
        this.isLoading = false;
        console.error('Extraction error:', err);
        const msg = err && (err as any).message ? (err as any).message : 'An error occurred while extracting trip details. Please try again.';
        this.errorMessage = msg;
        this.toastr.error('Failed to parse trip document', 'Error');
        this.cdr.detectChanges();
      });
    }
  }

  selectTrip(index: number) {
    this.selectedTripIndex = index;
  }

  confirmAndLoad() {
    if (!this.selectedFundingSourceId) {
      this.isFsRequiredError = true;
      this.toastr.warning('Funding Source is required to load trip data. Please select a Funding Source.', 'Funding Source Required');
      return;
    }
    this.isFsRequiredError = false;

    if (this.extractedTrips.length > 0 && this.selectedTripIndex >= 0 && this.selectedTripIndex < this.extractedTrips.length) {
      const chosenTrip = { ...this.extractedTrips[this.selectedTripIndex] };
      if (this.selectedFundingSourceId && this.fundingSources) {
        const selectedFs = this.fundingSources.find(f => f._id === this.selectedFundingSourceId);
        if (selectedFs) {
          chosenTrip.fundingSourceId = this.selectedFundingSourceId;
          chosenTrip.fundingSource = selectedFs.displayName || selectedFs.name || selectedFs.companyName || chosenTrip.fundingSource;
          chosenTrip.fundingSourceObj = selectedFs;
        }
      }
      if (this.selectedFile) {
        chosenTrip._rawFile = this.selectedFile;
      }
      this.onTripExtracted.emit(chosenTrip);
      this.close();
    }
  }

  resetFile() {
    this.selectedFile = null;
    this.extractedTrips = [];
    this.errorMessage = '';
    this.selectedTripIndex = 0;
    this.imagePreviewUrl = null;
    this.fileType = 'unknown';
    if (this.fileInput && this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  close() {
    this.onClose.emit();
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /**
   * Returns a complete list of all legs (Outbound, Return, Multi-Stop) for modal preview
   */
  getAllLegs(trip: ExtractedTripData): Array<{
    legNumber: number;
    title: string;
    tripId?: string;
    pickupTime?: string;
    appointmentTime?: string;
    isWillCall?: boolean;
    pickupAddress: string;
    pickupLocationName?: string;
    pickupRoom?: string;
    dropoffAddress: string;
    dropoffLocationName?: string;
    dropoffRoom?: string;
    notes?: string;
  }> {
    if (!trip) return [];

    const legsList: Array<any> = [];

    // 1. Leg 1 (Outbound / Main Trip)
    legsList.push({
      legNumber: 1,
      title: trip.isRoundTrip || (trip.legs && trip.legs.length > 0) ? 'Leg 1 (Outbound)' : 'Direct Trip',
      tripId: trip.tripId,
      pickupTime: trip.pickupTime,
      appointmentTime: trip.appointmentTime || trip.checkInTime,
      isWillCall: trip.isWillCall && !trip.isRoundTrip && (!trip.legs || trip.legs.length === 0),
      pickupAddress: trip.pickupAddress || 'Address not specified',
      pickupLocationName: trip.pickupLocationName,
      pickupRoom: trip.pickupRoom,
      dropoffAddress: trip.dropoffAddress || 'Address not specified',
      dropoffLocationName: trip.dropoffLocationName,
      dropoffRoom: trip.dropoffRoom,
      notes: trip.dispatchNote || trip.notes
    });

    // 2. Multi-leg list from trip.legs
    if (trip.legs && trip.legs.length > 0) {
      trip.legs.forEach((leg, idx) => {
        const legNum = idx + 2;
        legsList.push({
          legNumber: legNum,
          title: idx === 0 && trip.legs!.length === 1 ? 'Leg 2 (Return)' : `Leg ${legNum}`,
          tripId: leg.tripId,
          pickupTime: leg.pickupTime || (idx === 0 ? trip.returnPickupTime : undefined),
          appointmentTime: leg.appointmentTime,
          isWillCall: Boolean(leg.isWillCall),
          pickupAddress: leg.pickupAddress || trip.dropoffAddress || 'Address not specified',
          pickupLocationName: leg.pickupLocationName || trip.dropoffLocationName,
          pickupRoom: leg.pickupRoom,
          dropoffAddress: leg.dropoffAddress || trip.pickupAddress || 'Address not specified',
          dropoffLocationName: leg.dropoffLocationName || trip.pickupLocationName,
          dropoffRoom: leg.dropoffRoom,
          notes: leg.dispatchNote || leg.notes
        });
      });
    } else if (trip.isRoundTrip) {
      // 3. 2-way Round trip where return leg is reverse of Leg 1
      legsList.push({
        legNumber: 2,
        title: 'Leg 2 (Return)',
        tripId: trip.tripId ? `${trip.tripId}-B` : undefined,
        pickupTime: trip.returnPickupTime,
        appointmentTime: undefined,
        isWillCall: Boolean(trip.isWillCall || !trip.returnPickupTime),
        pickupAddress: trip.dropoffAddress || 'Address not specified',
        pickupLocationName: trip.dropoffLocationName,
        pickupRoom: trip.dropoffRoom,
        dropoffAddress: trip.pickupAddress || 'Address not specified',
        dropoffLocationName: trip.pickupLocationName,
        dropoffRoom: trip.pickupRoom,
        notes: trip.notes
      });
    }

    return legsList;
  }
}
